import ReviewsCarousel from './ReviewsCarousel';
import ReviewAuthor from './ReviewAuthor';
import { useState } from 'react';

import LeadForm from './LeadForm';
import PageMeta from './PageMeta';
import { RedesignFooter, RedesignHeader, RedesignPage, SiteLink } from './RedesignShell';

const proofItems = [
  'Договор с фиксированной ценой',
  'Сроки прописаны поэтапно',
  'Гарантия 24 месяца',
  'Базовое SEO в стоимости',
  'Персональный менеджер проекта',
  'Поддержка после запуска',
];

const taskItems = [
  'привлекать новых клиентов через органический поиск и рекламные кампании;',
  'формировать доверие у партнёров, инвесторов и потенциальных сотрудников;',
  'презентовать продукты и услуги с возможностью онлайн-заказа или обратного звонка;',
  'автоматизировать обработку заявок через интеграцию с CRM-системами;',
  'вести корпоративный блог и публиковать новости компании для работы с аудиторией;',
  'закрывать вакансии через встроенный раздел с формами отклика.',
];

const corporateTypes = [
  {
    title: 'Корпоративный сайт компании',
    text: 'Классический корпоративный сайт — имидж компании в интернете. Он рассказывает об услугах, команде, достижениях и открывает путь к первому обращению. Структура формируется индивидуально под специфику отрасли: строительство, медицина, производство, юридические услуги, IT. Базовый состав: главная страница, услуги или продукция, о компании, портфолио или кейсы, команда, новости, контактные данные и форма обратной связи. При необходимости добавляются вакансии, блог, акции и партнёрский раздел.',
  },
  {
    title: 'Корпоративный сайт с каталогом',
    text: 'Подходит компаниям, которые реализуют продукцию через интернет, но не нуждаются в полноценном интернет-магазине. Каталог товаров или услуг создаётся с удобными фильтрами, карточками продуктов, возможностью сравнения позиций и формой запроса прайса или коммерческого предложения. Такой формат часто выбирают производители, оптовые поставщики и B2B-компании. Интеграция с 1С позволяет автоматически обновлять остатки и цены без ручного вмешательства сотрудников.',
  },
  {
    title: 'Корпоративный сайт с блогом',
    text: 'Компании, которые делают ставку на контент-маркетинг, получают сайт с полноценной системой управления публикациями. Блог помогает привлекать органический трафик по информационным запросам, демонстрировать экспертизу и прогревать аудиторию перед покупкой. Раздел настраивается под разные форматы: статьи, кейсы, видеоразборы, новости отрасли. Каждая публикация оптимизируется под требования поисковых систем.',
  },
  {
    title: 'Персонализированные корпоративные сайты',
    text: 'Для компаний с нестандартными задачами разрабатываются персонализированные решения: корпоративные порталы с личными кабинетами для сотрудников или клиентов, многоязычные сайты для работы на международных рынках, платформы с интерактивными элементами, онлайн-калькуляторами и сложной логикой фильтрации. Разработка ведётся на основе детального технического задания. Функционал определяется на этапе аналитики — до начала проектирования.',
  },
];

const includedSteps = [
  ['Аналитика и проектирование', 'На старте проводится анализ бизнеса заказчика, целевой аудитории и конкурентов. Специалисты изучают поисковые запросы и формируют структуру сайта с учётом SEO. По результатам составляется техническое задание и создаются UX-прототипы — схемы расположения блоков на каждой странице.'],
  ['UX/UI дизайн', 'На основе утверждённых прототипов разрабатываются дизайн-макеты в Figma. Дизайн соответствует фирменному стилю компании и решает задачи конверсии. Все правки вносятся до перехода к вёрстке — это экономит время и бюджет.'],
  ['Разработка и программирование', 'Вёрстка ведётся с соблюдением семантики HTML и требований поисковых систем. Программирование охватывает как фронтенд, так и бэкенд: серверная логика, база данных, API-интеграции, настройка CMS. Адаптивная вёрстка обеспечивает корректное отображение на компьютерах, планшетах и мобильных устройствах.'],
  ['SEO-оптимизация', 'Базовая SEO-оптимизация входит в стоимость разработки. В неё включено: настройка мета-тегов, ЧПУ-адреса, заголовки H1–H3, микроразметка Schema.org, файлы sitemap.xml и robots.txt, оптимизация скорости загрузки страниц. Сайт передаётся готовым к продвижению с первого дня.'],
  ['Тестирование и запуск', 'Перед запуском сайт проходит многоуровневое тестирование: проверяется работа форм, корректность отображения в разных браузерах, скорость загрузки, работа интеграций. После устранения ошибок сайт переносится на хостинг, подключаются системы аналитики — Яндекс.Метрика и Google Analytics.'],
];

const technologies = [
  ['Frontend', 'Фронтенд разрабатывается на React или Vue.js. Все сайты проходят кроссбраузерное тестирование и адаптируются под мобильные устройства, планшеты и компьютеры. Скорость загрузки оптимизируется до показателей Core Web Vitals.'],
  ['Backend', 'Серверная часть создаётся на PHP, Python или Node.js в зависимости от требований проекта. Для хранения данных используются реляционные (MySQL, PostgreSQL) и нереляционные базы. Безопасность данных и устойчивость под нагрузкой обеспечиваются на уровне архитектуры.'],
  ['CMS и фреймворки', 'Для корпоративных сайтов чаще всего используется 1С-Битрикс (Bitrix) — это мощная платформа с развитой экосистемой интеграций. WordPress подходит для контентных проектов и блогов. Для нестандартных задач создаются решения на собственном фреймворке с удобной административной панелью.'],
  ['Интеграции и API', 'Корпоративный сайт интегрируется с CRM-системами (AmoCRM, Битрикс24), сервисами аналитики (Яндекс.Метрика, Google Analytics), 1С, платёжными шлюзами, службами доставки, Telegram и другими сторонними сервисами через API. Это позволяет автоматизировать бизнес-процессы и синхронизировать данные в режиме реального времени.'],
];

const creationSteps = [
  ['Анализ и бриф', 'Работа начинается с заполнения брифа. Менеджер проекта проводит брифинг: изучает бизнес, задачи сайта, целевую аудиторию и конкурентов. По результатам формируется техническое задание — основной документ разработки.'],
  ['Проектирование структуры', 'Специалисты разрабатывают карту сайта и UX-прототипы для каждой страницы. Это позволяет клиенту увидеть логику ресурса и внести правки до начала визуальной работы над дизайном.'],
  ['Дизайн', 'На основе утверждённых прототипов создаются дизайн-макеты в Figma. Дизайн разрабатывается с учётом фирменного стиля, психологии восприятия и задач конверсии. После согласования всех макетов стартует вёрстка.'],
  ['Разработка', 'Вёрстка и программирование ведутся по утверждённому ТЗ. Команда работает итерациями — промежуточные результаты показываются заказчику по мере готовности. Это сокращает количество правок после сдачи и ускоряет разработку.'],
  ['Запуск и оптимизация', 'После тестирования сайт переносится на хостинг. Подключаются системы аналитики, настраиваются поисковые инструменты, проверяется индексация. Заказчик получает доступ к панели администратора и инструкцию по управлению сайтом.'],
];

const collaborationFormats = [
  ['Проектная разработка', 'Подходит для компаний с чётким техническим заданием. Стоимость и сроки фиксируются до начала работ. Команда берёт проект от аналитики до запуска и сдаёт готовый корпоративный сайт под ключ.'],
  ['Выделенная команда', 'Формат для крупных и долгосрочных проектов. Заказчик получает выделенного разработчика, дизайнера и менеджера, которые полностью погружены в задачи бизнеса. Команда работает как внутренний IT-отдел без накладных расходов на найм в штат.'],
  ['Почасовая работа', 'Оптимальный вариант для точечных доработок существующего корпоративного сайта. Оплата ведётся по факту отработанных часов. Удобно, когда объём задач нестабилен или проект развивается небольшими шагами.'],
];

const deliverables = [
  ['Готовый корпоративный сайт', 'Сайт передаётся с заполненным контентом (при наличии у заказчика материалов), настроенными формами обратной связи и аналитикой. Менеджер проводит приёмку по чек-листу и фиксирует соответствие ТЗ.'],
  ['SEO-оптимизацию', 'Базовая SEO-оптимизация — в стоимости разработки. Это мета-теги, корректные заголовки, ЧПУ, микроразметка, sitemap и robots.txt. Сайт индексируется поисковыми системами сразу после запуска.'],
  ['Удобную админ-панель', 'Управление сайтом не требует технических знаний. Сотрудники компании самостоятельно добавляют новости, обновляют информацию об услугах, публикуют вакансии и отвечают на заявки через удобный интерфейс CMS.'],
  ['Масштабируемую архитектуру', 'Сайт проектируется с расчётом на развитие. Новые разделы, функциональные модули и интеграции добавляются без переработки базовой архитектуры. Это сохраняет инвестиции в разработку при масштабировании бизнеса.'],
];

const supportItems = [
  ['Техническая поддержка', 'Специалисты следят за работоспособностью сайта, своевременно обновляют CMS и плагины, устраняют технические сбои. Обращения принимаются по телефону, e-mail и в Telegram.'],
  ['SLA и гарантии', 'Гарантийный срок на разработанный функционал — 24 месяца. Время реакции на критические обращения — не более 4 часов. Условия SLA фиксируются в договоре поддержки до подписания.'],
  ['Доработки', 'Бизнес меняется — сайт обновляется вместе с ним. Команда берёт в работу задачи любого объёма: редизайн отдельных страниц, добавление новых разделов, подключение дополнительных интеграций, обновление контента.'],
  ['SEO и продвижение', 'После запуска доступна услуга полноценного SEO-продвижения. Специалисты проводят аудит, собирают семантическое ядро, оптимизируют страницы под поисковые запросы и выстраивают стратегию роста позиций в Яндексе и Google.'],
];

const priceRows = [
  ['Корпоративный сайт-визитка (5–10 страниц)', '2–3 недели', 'от 40.000₽'],
  ['Корпоративный сайт (до 30 страниц)', '4–6 недель', 'от 55.000₽'],
  ['Корпоративный сайт с каталогом', '6–8 недель', 'от 70.000₽'],
  ['Корпоративный портал (личные кабинеты, сложный функционал)', 'от 2 месяцев', 'от 150.000₽'],
];

const relatedServices = [
  { href: 'https://fatalitystudio.ru/services/websites/landing/', title: 'Разработка лендинга' },
  { href: 'https://fatalitystudio.ru/services/mobile/', title: 'Разработка приложений' },
  { href: 'https://fatalitystudio.ru/services/seo/technical/', title: 'Техническое Seo' },
  { href: 'https://fatalitystudio.ru/services/websites/redesign-support/', title: 'Редизайн и поддержка' },
];

const reviews = [
  {
    name: 'Алексей М.',
    role: 'директор производственной компании',
    full: 'Алексей М.,директор производственной компании',
    text: 'Заказывали корпоративный сайт с каталогом продукции. Команда чётко выдержала сроки, все правки вносились быстро. Особенно понравилась работа с техническим заданием, ребята всё учли до мелочей. Уже через два месяца после запуска начали получать заявки с органического поиска.',
  },
  {
    name: 'Елена К.',
    role: 'маркетолог медицинской клиники',
    full: 'Елена К., маркетолог медицинской клиники',
    text: 'Хотели сделать редизайн существующего сайта. Боялись потерять позиции в Яндексе, но специалисты грамотно перенесли всё с сохранением SEO и создали по сути новый сайт. Сайт стал работать быстрее, заявок стало больше.',
  },
];

const faqItems = [
  ['Сколько стоит разработка корпоративного сайта?', 'Стоимость зависит от типа проекта. Небольшой корпоративный сайт обойдётся от 40.000 ₽, сайт с каталогом и интеграциями — от 70.000 ₽, портал с личными кабинетами — от 150.000 ₽. Точный расчёт предоставляется бесплатно после брифинга — оставьте заявку, и менеджер свяжется с вами в ближайшее время.'],
  ['Каковы сроки создания?', 'Корпоративный сайт-визитка разрабатывается за 2–3 недели. Стандартный корпоративный сайт до 30 страниц — за 4–6 недель. Сайт с каталогом и интеграциями — за 6–8 недель. Сроки фиксируются в договоре и зависят от сложности проекта и скорости согласований с заказчиком.'],
  ['Что нужно для старта?', 'Для начала работы достаточно заполнить бриф: рассказать о компании, задачах сайта и целевой аудитории. Анализ конкурентов, проектирование структуры и подготовку ТЗ команда берёт на себя. Если есть фирменный стиль и логотип — пришлите, это ускорит этап дизайна. С Вас - вовлечение, с нас - результат и радость от Вашей прибыли и роста.'],
  [
    'Как продвигать корпоративный сайт?',
    <>
      Базовая SEO-оптимизация входит в стоимость разработки. Для активного
      продвижения в поисковой выдаче и получения стабильного трафика доступна
      отдельная услуга — <SiteLink href="https://fatalitystudio.ru/services/seo/full-cycle/">SEO-продвижение сайта</SiteLink>.
      Специалисты проведут аудит, соберут семантику и разработают стратегию
      роста позиций.
    </>,
  ],
];

function SectionHeader({ title, description, dark = false }) {
  return (
    <div className={`sec-head${dark ? ' ink-head' : ''}`}>
      <div>
        <h2>{title}</h2>
      </div>
      {description && <p className="sub">{description}</p>}
    </div>
  );
}

function CorporateWebsitesPage() {
  const [activeType, setActiveType] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <RedesignPage>
      <PageMeta
        title="Разработка корпоративного сайта под ключ - заказать создание корпоративных веб - сайтов для компании"
        description="Закажите разработку корпоративного интернет-сайта любой сложности под ключ. Создание корпоративного веб-сайта на заказ по выгодной цене и под цели Вашего бизнеса.Индивидуальный дизайн, системный подход, множество кейсов и команда профессионалов. Рассчитайте стоимость в студии fatalitystudio.ru или по телефону +7 (925) 653-04-54 !"
      />
      <RedesignHeader />

      <section className="ink service-hero">
        <div
          className="glow"
          style={{ right: '-360px', top: '-340px', width: '980px', height: '980px', opacity: 0.1 }}
        />
        <div className="container">
          <div>
            <h1>Разработка корпоративного сайта под ключ</h1>
            <p className="hero-sub">
              Корпоративный сайт — это не просто страница в интернете. Это
              инструмент, который формирует доверие к компании, привлекает
              клиентов и партнёров, автоматизирует обработку обращений.
            </p>
            <p className="service-hero-copy">
              Команда разрабатывает корпоративные сайты с нуля: от аналитики и
              проектирования до запуска и SEO-продвижения.
            </p>
          </div>
        </div>
      </section>

      <section className="canvas sec-pad">
        <div className="container">
          <SectionHeader
            eyebrow="— бизнес"
            title="Разработка корпоративного сайта для бизнеса"
            description="Каждый проект начинается с погружения в задачи бизнеса. Специалисты изучают нишу, целевую аудиторию и конкурентов — это позволяет создать сайт, который решает конкретные коммерческие задачи, а не просто выглядит красиво."
          />
          <p className="service-hero-copy" style={{ color: '#141619' }}>
            Команда работает с проектами любой сложности: от корпоративных
            сайтов небольших компаний до многостраничных порталов с личными
            кабинетами, каталогами продукции и интеграцией с 1С-Битрикс. Каждый
            сайт адаптируется под мобильные устройства и соответствует
            требованиям поисковых систем Яндекс и Google.
          </p>
          <div className="proof-strip">
            {proofItems.map((item) => (
              <article className="proof-item" key={item}>
                <span className="tz-card-icon" aria-hidden="true" />
                <h3>{item}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="canvas sec-pad section-line-top">
        <div className="container">
          <SectionHeader
            eyebrow="— задачи"
            title="Какие задачи решает корпоративный сайт"
            description="Компании заказывают создание корпоративного сайта для решения разных бизнес-задач. Понимание этих задач на старте определяет структуру, функционал и дизайн будущего ресурса."
          />
          <div className="split-proof">
            <div>
              <p>Корпоративный сайт позволяет:</p>
              <ul className="source-list">
                {taskItems.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <p>
                Правильно спроектированный корпоративный веб-сайт снижает
                нагрузку на отдел продаж и становится самостоятельным каналом
                привлечения клиентов.
              </p>
              <SiteLink href="#corporate-lead" className="btn btn-primary" style={{ marginTop: '32px' }}>
                От идеи до разработкм -Fatality»
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      <section className="ink sec-pad">
        <div className="container">
          <SectionHeader
            eyebrow="— форматы"
            title="Какие корпоративные сайты мы разрабатываем"
            description="Тип корпоративного сайта определяется задачами бизнеса. Ниже — основные форматы, с которыми работает команда."
            dark
          />
          <div className="corp-type-layout">
            <div className="corp-type-tabs" role="tablist" aria-label="Типы корпоративных сайтов">
              {corporateTypes.map((item, index) => (
                <button
                  className={`corp-type-trigger${activeType === index ? ' active' : ''}`}
                  type="button"
                  onClick={() => setActiveType(index)}
                  role="tab"
                  id={`corporate-type-tab-${index}`}
                  aria-controls={`corporate-type-panel-${index}`}
                  aria-selected={activeType === index}
                  tabIndex={activeType === index ? 0 : -1}
                  key={item.title}
                >
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  {item.title}
                </button>
              ))}
            </div>
            <div>
              {corporateTypes.map((item, index) => (
                <div
                  className="corp-type-detail"
                  role="tabpanel"
                  id={`corporate-type-panel-${index}`}
                  aria-labelledby={`corporate-type-tab-${index}`}
                  hidden={activeType !== index}
                  key={item.title}
                >
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  <SiteLink href="#corporate-lead" className="link-flat">Узнать стоимость</SiteLink>
                  {/* TODO(brand-redesign): получить утверждённую референсную картинку или скриншот этого типа корпоративного сайта. */}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="canvas sec-pad">
        <div className="container">
          <SectionHeader
            eyebrow="— состав работ"
            title="Что входит в разработку корпоративного сайта"
            description="Создание корпоративного сайта — это комплексная услуга. Каждый этап взаимосвязан и влияет на финальный результат."
          />
          <ol className="tz-stage-timeline" aria-label="Этапы разработки корпоративного сайта">
            {includedSteps.map(([title, text], index) => (
              <li className="tz-stage-step" key={title}>
                <span className="tz-stage-icon" aria-hidden="true">{index + 1}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </li>
            ))}
          </ol>
          {/* TODO(brand-redesign): получить сроки выполнения в рабочих днях для каждого этапа; значения в ТЗ отсутствуют. */}
          <SiteLink href="#corporate-lead" className="btn btn-primary section-action">Начать проект</SiteLink>
        </div>
      </section>

      <section className="ink sec-pad">
        <div className="container">
          <SectionHeader
            eyebrow="— технологии"
            title="Технологии разработки корпоративных сайтов"
            description="Выбор технологического стека влияет на производительность, стоимость поддержки и возможности масштабирования корпоративного сайта. Команда использует проверенные инструменты, которые соответствуют задачам конкретного проекта."
            dark
          />
          <div className="tech-tiles">
            {technologies.map(([title, text]) => (
              <div className="tech-tile" key={title}>
                <span className="tz-card-icon" aria-hidden="true" />
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
          {/* TODO(brand-redesign): получить source-backed логотипы React, Vue, PHP, 1С-Битрикс, WordPress и Figma. */}
        </div>
      </section>

      <section className="canvas sec-pad">
        <div className="container">
          <SectionHeader
            eyebrow="— процесс"
            title="Как проходит создание корпоративного сайта"
            description="Процесс разработки корпоративного сайта состоит из пяти последовательных этапов. Заказчик участвует в согласованиях на каждом шаге — никаких сюрпризов на финале."
          />
          <div className="process-rail process-rail-five">
            {creationSteps.map(([title, text]) => (
              <div className="process-step" key={title}>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="canvas sec-pad section-line-top">
        <div className="container">
          <SectionHeader
            eyebrow="— подход"
            title="Подход к разработке корпоративных сайтов"
            description="Подход к каждому проекту определяет, уложится ли команда в сроки и бюджет. Здесь — три принципа, которые защищают интересы заказчика."
          />
          <div className="compact-card-grid">
            {[
              ['Agile / Scrum', 'Разработка ведётся короткими итерациями — спринтами по одной-две недели. После каждого спринта заказчик видит реальный результат и принимает решения на основе готового функционала, а не описания в ТЗ.'],
              ['Прозрачные этапы', 'Перед стартом подписывается договор с фиксированными сроками и стоимостью. Дорожная карта проекта разбита по этапам с конкретными датами. Еженедельно предоставляется отчёт о ходе работ. Всё общение с менеджером — в одном канале, без потери информации.'],
              ['Фокус на конверсии', 'Каждый элемент сайта оценивается с точки зрения конверсии. Расположение форм, тексты кнопок, структура страниц — всё строится так, чтобы посетитель совершил целевое действие. При необходимости проводится A/B тестирование ключевых блоков после запуска.'],
            ].map(([title, text]) => (
              <div className="compact-card" key={title}>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ink sec-pad">
        <div className="container">
          <SectionHeader
            eyebrow="— сотрудничество"
            title="Форматы сотрудничества"
            description="Компания предлагает три формата работы в зависимости от масштаба проекта и ресурсов заказчика."
            dark
          />
          <div className="compact-card-grid">
            {collaborationFormats.map(([title, text]) => (
              <div className="compact-card interactive-card" key={title}>
                <span className="tz-card-icon" aria-hidden="true" />
                <h3>{title}</h3>
                <p>{text}</p>
                <SiteLink href="#corporate-lead" className="link-flat">
                  Выбрать этот формат
                </SiteLink>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="canvas sec-pad">
        <div className="container">
          <SectionHeader
            eyebrow="— итог"
            title="Что вы получаете в итоге"
            description="После завершения разработки заказчик получает готовый цифровой продукт — корпоративный сайт, который работает на бизнес с первого дня."
          />
          <div className="compact-card-grid">
            {deliverables.map(([title, text]) => (
              <div className="compact-card" key={title}>
                <span className="tz-card-icon" aria-hidden="true" />
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
          <SiteLink href="#corporate-lead" className="btn btn-primary section-action">
            Заказать корпоративный сайт
          </SiteLink>
        </div>
      </section>

      <section className="canvas sec-pad section-line-top">
        <div className="container">
          <SectionHeader
            eyebrow="— поддержка"
            title="Поддержка и развитие корпоративного сайта"
            description="Запуск сайта — начало, а не финал. Команда обеспечивает техническое сопровождение и развитие проекта на всех этапах его жизни."
          />
          <div className="compact-card-grid">
            {supportItems.map(([title, text]) => (
              <div className="compact-card" key={title}>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="canvas sec-pad section-line-top">
        <div className="container">
          <SectionHeader
            eyebrow="— стоимость"
            title="Стоимость разработки корпоративного сайта"
            description="Цена на разработку корпоративного сайта зависит от задач проекта. Ниже — ключевые факторы и ориентиры."
          />
          <div className="pricing-layout">
            <div className="price-note">
              <h3>От чего зависит цена</h3>
              <p>На стоимость влияют:</p>
              <ul className="price-factors">
                <li>тип и масштаб сайта (визитка, корпоративный, портал с личными кабинетами);</li>
                <li>количество страниц и разделов;</li>
                <li>уникальный дизайн или адаптация шаблона;</li>
                <li>необходимый функционал: каталог, фильтры, калькулятор, личный кабинет;</li>
                <li>интеграции с CRM, 1С, платёжными системами и другими сервисами;</li>
                <li>выбор CMS или разработка на собственном фреймворке;</li>
                <li>сроки — стандартные или сжатые.</li>
              </ul>
              <h3>Как рассчитывается стоимость</h3>
              <p>
                После получения брифа менеджер декомпозирует проект на задачи и
                оценивает трудозатраты по каждому блоку. Заказчик получает
                детальную смету с разбивкой по этапам. Стоимость фиксируется в
                договоре — никаких дополнительных счетов в процессе работы.
              </p>
              <h3>Получить расчёт</h3>
              <p>
                Оставьте заявку — менеджер свяжется в течение рабочего дня и
                подготовит предварительный расчёт стоимости бесплатно.
              </p>
              {/* TODO(brand-redesign): получить утверждённые фото и имя менеджера для source trust-блока. */}
              <LeadForm
                formId="corporate-calc-form"
                idPrefix="corporate-calc"
                eyebrow="Форма заявки"
                contactLabel="Телефон или E-mail"
                contactType="text"
                contactPlaceholder="Телефон или E-mail"
                showTelegram={false}
                showCompany={false}
                typeOptions={corporateTypes.map(({ title }) => title)}
                typeLabel="Тип сайта"
                submitLabel="Получить расчёт бесплатно"
                taskPlaceholder="Комментарий"
              />
            </div>
            <div className="price-list">
              <h3 className="pricing-subhead">Примерные цены</h3>
              <p>Ориентировочная стоимость разработки корпоративных сайтов:</p>
              <div className="price-table-scroll">
                <table className="tz-price-table">
                  <thead>
                    <tr>
                      <th scope="col">Тип сайта</th>
                      <th scope="col">Срок</th>
                      <th scope="col">Стоимость</th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceRows.map(([type, time, price]) => (
                      <tr key={type}>
                        <th scope="row">{type}</th>
                        <td>{time}</td>
                        <td>{price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="canvas sec-pad section-line-top">
        <div className="container">
          <SectionHeader
            eyebrow="— рядом"
            title="Похожие услуги"
          />
          <div className="related-grid">
            {relatedServices.map((item) => (
              <SiteLink href={item.href} className="related-card" key={item.title}>
                <span>{item.title}</span>
              </SiteLink>
            ))}
          </div>
        </div>
      </section>

      <section className="ink sec-pad">
        <div className="container">
          <SectionHeader
            eyebrow="— кейс"
            title="Кейсы разработки корпоративных сайтов"
            description="Результаты работы показывают лучше, чем любые слова. Ниже — реальные проекты с конкретными показателями."
            dark
          />
          <h3 className="standalone-h3 ink-standalone">Примеры с результатами</h3>
          <SiteLink href="/portfolio/sokolniki-restaurant/" className="case-teaser">
            <div className="case-teaser-body">
              <strong>Разработка корпоративного сайта ресторана в Сокольниках под ключ</strong>
              <p>
                Проект включал полный цикл: создание сайта ресторана с нуля,
                разработку индивидуальной CMS, SEO-оптимизацию и запуск рекламы.
              </p>
            </div>
          </SiteLink>
          {/* TODO(brand-redesign): получить source-backed фотографию кейса; вложения ТЗ являются логотипом и подписью. */}
        </div>
      </section>

      <section className="canvas sec-pad">
        <div className="container">
          <SectionHeader
            eyebrow="— отзывы"
            title="Отзывы клиентов: от идеи - к готовому корпоративному сайту компании"
          />
          <ReviewsCarousel className="reviews-grid reviews-grid-two">
            {reviews.map((review) => (
              <figure className="review-card" key={review.name}>
                <blockquote>{review.text}</blockquote>
                <ReviewAuthor label={review.full} />
              </figure>
            ))}
          </ReviewsCarousel>
        </div>
      </section>

      <section className="canvas sec-pad section-line-top">
        <div className="container">
          <div className="faq-grid">
            <div>
              <h2 className="standalone-h2">Ответы на частые вопросы</h2>
            </div>
            <div className="faq-list">
              {faqItems.map(([question, answer], index) => {
                const isOpen = openFaq === index;
                return (
                  <div className={`faq-item${isOpen ? ' open' : ''}`} key={question}>
                    <h3>
                      <button
                        className="faq-q"
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? -1 : index)}
                        aria-expanded={isOpen}
                        aria-controls={`corporate-faq-answer-${index}`}
                      >
                        <span>{question}</span>
                        <span className="faq-toggle" aria-hidden="true" />
                      </button>
                    </h3>
                    <div className="faq-a" id={`corporate-faq-answer-${index}`} aria-hidden={!isOpen}>
                      <div className="faq-a-inner">{answer}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="ink lead" id="corporate-lead">
        <div
          className="glow"
          style={{ left: '-300px', top: '-200px', width: '700px', height: '700px', opacity: 0.1 }}
        />
        <div className="container" style={{ position: 'relative' }}>
          <div className="lead-grid">
            <div className="lead-side">
              <h2 className="standalone-h2">Заказать разработку корпоративного сайта</h2>
              <p className="lead-text">
                Расскажите о своём проекте — команда подготовит предварительную
                смету и ответит на вопросы в течение рабочего дня.
              </p>
              <p className="lead-heading">Получите расчёт стоимости за 1 день</p>
              <p className="lead-text">
                Расскажите о проекте. Подготовим предварительную смету, покажем
                состав работ и ответим на вопросы в течение рабочего дня.
              </p>
              <p className="lead-text">Предпочитаете позвонить? +7 (XXX) XXX-XX-XX</p>
            </div>
            <LeadForm
              formId="corporate-lead-form"
              idPrefix="corporate"
              eyebrow="Форма заявки"
              contactLabel="Телефон или E-mail"
              contactType="text"
              contactPlaceholder="Телефон или E-mail"
              showTelegram={false}
              showCompany={false}
              typeOptions={corporateTypes.map(({ title }) => title)}
              typeLabel="Тип сайта"
              submitLabel="Отправить заявку"
              taskPlaceholder="Краткое описание задачи"
            />
          </div>
        </div>
      </section>

      <RedesignFooter />
    </RedesignPage>
  );
}

export default CorporateWebsitesPage;
